import { useEffect, useMemo, useState } from 'react';

import MobileArticleShare from '@components/UserOnboardingTooltip/MobileSteps/ArticleShare';
import MobileDeepResearchAndSearch from '@components/UserOnboardingTooltip/MobileSteps/DeepResearchAndSearch';
import MobileDownloadApp from '@components/UserOnboardingTooltip/MobileSteps/DownloadApp';
import MobileNewsletters from '@components/UserOnboardingTooltip/MobileSteps/Newsletters';
import MobileProFeatures from '@components/UserOnboardingTooltip/MobileSteps/ProFeatures';
import MobileSettings from '@components/UserOnboardingTooltip/MobileSteps/Settings';
import MobileTitv from '@components/UserOnboardingTooltip/MobileSteps/Titv';
import MobileWelcome from '@components/UserOnboardingTooltip/MobileSteps/Welcome';
import ArticleShare from '@components/UserOnboardingTooltip/Steps/ArticleShare';
import DeepResearchAndSearch from '@components/UserOnboardingTooltip/Steps/DeepResearchAndSearch';
import DownloadApp from '@components/UserOnboardingTooltip/Steps/DownloadApp';
import Newsletters from '@components/UserOnboardingTooltip/Steps/Newsletters';
import ProFeatures from '@components/UserOnboardingTooltip/Steps/ProFeatures';
import Settings from '@components/UserOnboardingTooltip/Steps/Settings';
import Titv from '@components/UserOnboardingTooltip/Steps/Titv';
import Welcome from '@components/UserOnboardingTooltip/Steps/Welcome';
import {
  NEWSLETTER_ANCHOR_SELECTOR,
  NEWSLETTER_HOVER_CLASS,
  ONBOARDING_STEP_IDS,
} from '@components/UserOnboardingTooltip/constants';
import { StepTuple } from '@components/UserOnboardingTooltip/constants';
import { setActiveOnboardingStep } from '@components/UserOnboardingTooltip/utils';
import {
  getMoreNavRef,
  getOnboardingAnchor,
  getProNavRef,
  getSettingsNavRef,
} from '@components/UserOnboardingTooltip/utils';

import useAnchorRegisterStep from './useAnchorRegisterStep';
import useNavAnchorStep from './useNavAnchorStep';

export type PopperPlacement =
  | 'bottom'
  | 'bottom-start'
  | 'left-start'
  | 'right-start'
  | 'bottom-end';

export interface PopperStepConfig {
  stepId: (typeof ONBOARDING_STEP_IDS)[keyof typeof ONBOARDING_STEP_IDS];
  getAnchor: () => HTMLElement | null;
  placement?: PopperPlacement;
  offset?: [number, number];
}

export interface UseOnboardingPopperStepFlags {
  isWelcomeStep: boolean;
  isProFeaturesStep: boolean;
  isDeepResearchStep: boolean;
  isNewslettersStep: boolean;
  isTitvStep: boolean;
  isSettingsStep: boolean;
  isArticleShareStep: boolean;
  isDownloadAppStep: boolean;
}

export interface UseOnboardingPopperResult {
  popperConfig: PopperStepConfig | undefined;
  popperAnchor: HTMLElement | null;
  spotlightEls: HTMLElement[];
  useCenterAnchor: boolean;
  setCenterAnchorEl: (el: HTMLElement | null) => void;
}

const useOnboardingPopper = (
  step: number,
  steps: StepTuple,
  isMobile: boolean
): UseOnboardingPopperResult => {
  const currentStepComponent = steps[step];
  const isWelcomeStep = currentStepComponent === Welcome || currentStepComponent === MobileWelcome;
  const isProFeaturesStep =
    currentStepComponent === ProFeatures || currentStepComponent === MobileProFeatures;
  const isDeepResearchStep =
    currentStepComponent === DeepResearchAndSearch ||
    currentStepComponent === MobileDeepResearchAndSearch;
  const isNewslettersStep =
    currentStepComponent === Newsletters || currentStepComponent === MobileNewsletters;
  const isTitvStep = currentStepComponent === Titv || currentStepComponent === MobileTitv;
  const isSettingsStep =
    currentStepComponent === Settings || currentStepComponent === MobileSettings;
  const isArticleShareStep =
    currentStepComponent === ArticleShare || currentStepComponent === MobileArticleShare;
  const isDownloadAppStep =
    currentStepComponent === DownloadApp || currentStepComponent === MobileDownloadApp;

  const activeStepId = isWelcomeStep
    ? ONBOARDING_STEP_IDS.Welcome
    : isProFeaturesStep
    ? ONBOARDING_STEP_IDS.ProFeatures
    : isDeepResearchStep
    ? ONBOARDING_STEP_IDS.DeepResearchAndSearch
    : isNewslettersStep
    ? ONBOARDING_STEP_IDS.Newsletters
    : isTitvStep
    ? ONBOARDING_STEP_IDS.Titv
    : isSettingsStep
    ? ONBOARDING_STEP_IDS.Settings
    : isArticleShareStep
    ? ONBOARDING_STEP_IDS.ArticleShare
    : isDownloadAppStep
    ? ONBOARDING_STEP_IDS.DownloadApp
    : null;

  useEffect(() => {
    setActiveOnboardingStep(activeStepId);
    return () => setActiveOnboardingStep(null);
  }, [activeStepId]);

  /** When mobile, popper is not used (tooltip is centered in viewport). Disable all anchor logic. */
  const disabled = isMobile;
  const isProFeaturesStepActive = isProFeaturesStep && !disabled;
  const isDeepResearchStepActive = isDeepResearchStep && !disabled;
  const isNewslettersStepActive = isNewslettersStep && !disabled;
  const isTitvStepActive = isTitvStep && !disabled;
  const isSettingsStepActive = isSettingsStep && !disabled;
  const isArticleShareStepActive = isArticleShareStep && !disabled;

  /** ---------------------------
   * Center anchor (welcome / download)
   * ---------------------------- */
  const [centerAnchorEl, setCenterAnchorEl] = useState<HTMLElement | null>(null);

  /** ---------------------------
   * Pro Features
   * ---------------------------- */
  const { anchor: proNavAnchor, navEl: proNavEl } = useNavAnchorStep(
    isProFeaturesStepActive,
    getProNavRef,
    (ref) => ref.getSubmenuElement()
  );

  /** ---------------------------
   * Deep Research
   * ---------------------------- */
  const deepResearchAnchor = useAnchorRegisterStep(
    isDeepResearchStepActive,
    ONBOARDING_STEP_IDS.DeepResearchAndSearch,
    getOnboardingAnchor
  );

  /** ---------------------------
   * Newsletters (More nav + hover state)
   * ---------------------------- */
  const { anchor: newsletterAnchor, navEl: newsletterNavEl } = useNavAnchorStep(
    isNewslettersStepActive,
    getMoreNavRef,
    (ref) => {
      const submenu = ref.getSubmenuElement();
      const anchor = submenu?.querySelector<HTMLElement>(NEWSLETTER_ANCHOR_SELECTOR);

      if (anchor) {
        anchor.classList.add(NEWSLETTER_HOVER_CLASS);
      }

      return anchor;
    },
    {
      cleanup(ref) {
        const submenu = ref?.getSubmenuElement();
        const anchor = submenu?.querySelector<HTMLElement>(NEWSLETTER_ANCHOR_SELECTOR);
        anchor?.classList.remove(NEWSLETTER_HOVER_CLASS);
      },
    }
  );

  /** ---------------------------
   * Titv
   * ---------------------------- */
  const titvAnchor = useAnchorRegisterStep(
    isTitvStepActive,
    ONBOARDING_STEP_IDS.Titv,
    getOnboardingAnchor
  );

  /** ---------------------------
   * Settings
   * ---------------------------- */
  const { anchor: settingsAnchor, navEl: settingsNavEl } = useNavAnchorStep(
    isSettingsStepActive,
    getSettingsNavRef,
    (ref) => ref.getSubmenuElement()
  );

  /** ---------------------------
   * Article Share (share button in article body)
   * ---------------------------- */
  const articleShareAnchor = useAnchorRegisterStep(
    isArticleShareStepActive,
    ONBOARDING_STEP_IDS.ArticleShare,
    getOnboardingAnchor
  );

  /** ---------------------------
   * Popper config mapping
   * ---------------------------- */
  const popperStepConfigs: PopperStepConfig[] = useMemo(
    () =>
      disabled
        ? []
        : [
            {
              stepId: ONBOARDING_STEP_IDS.Welcome,
              getAnchor: () => centerAnchorEl,
              placement: 'bottom',
            },
            {
              stepId: ONBOARDING_STEP_IDS.ProFeatures,
              getAnchor: () => proNavAnchor,
            },
            {
              stepId: ONBOARDING_STEP_IDS.DeepResearchAndSearch,
              getAnchor: () => deepResearchAnchor,
              placement: 'bottom-end',
            },
            {
              stepId: ONBOARDING_STEP_IDS.Newsletters,
              getAnchor: () => newsletterAnchor,
              placement: 'left-start',
              offset: [0, 32],
            },
            {
              stepId: ONBOARDING_STEP_IDS.Titv,
              getAnchor: () => titvAnchor,
              placement: 'bottom-end',
            },
            {
              stepId: ONBOARDING_STEP_IDS.Settings,
              getAnchor: () => settingsAnchor,
              placement: 'left-start',
              offset: [0, 4],
            },
            {
              stepId: ONBOARDING_STEP_IDS.ArticleShare,
              getAnchor: () => articleShareAnchor,
              placement: 'bottom-end',
              offset: [0, 4],
            },
            {
              stepId: ONBOARDING_STEP_IDS.DownloadApp,
              getAnchor: () => centerAnchorEl,
              placement: 'bottom',
              offset: [0, 4],
            },
          ],
    [
      disabled,
      centerAnchorEl,
      proNavAnchor,
      deepResearchAnchor,
      newsletterAnchor,
      titvAnchor,
      settingsAnchor,
      articleShareAnchor,
    ]
  );

  const popperConfig = useMemo(
    () => popperStepConfigs.find((config) => config.stepId === activeStepId && config.getAnchor()),
    [popperStepConfigs, activeStepId]
  );

  const spotlightEls: HTMLElement[] = [];
  if (!disabled) {
    if (isProFeaturesStep) {
      const panel = document.getElementById('pro-submenu-nav-list');
      if (proNavEl) spotlightEls.push(proNavEl);
      if (panel) spotlightEls.push(panel);
    } else if (isNewslettersStep) {
      const panel = document.getElementById('more-submenu-nav-list');
      if (newsletterNavEl) spotlightEls.push(newsletterNavEl);
      if (panel) spotlightEls.push(panel);
    } else if (isSettingsStep) {
      const panel = document.getElementById('settings-submenu-nav-list');
      if (settingsNavEl) spotlightEls.push(settingsNavEl);
      if (panel) spotlightEls.push(panel);
    } else if (isDeepResearchStep && deepResearchAnchor) {
      spotlightEls.push(deepResearchAnchor);
    } else if (isTitvStep && titvAnchor) {
      spotlightEls.push(titvAnchor);
    } else if (isArticleShareStep && articleShareAnchor) {
      spotlightEls.push(articleShareAnchor);
    }
  }

  return {
    popperConfig: disabled ? undefined : popperConfig,
    popperAnchor: disabled ? null : popperConfig?.getAnchor() ?? null,
    spotlightEls,
    useCenterAnchor: disabled ? false : isWelcomeStep || isDownloadAppStep,
    setCenterAnchorEl,
  };
};

export default useOnboardingPopper;
